<script setup lang="ts">
import { PrismEditor } from 'vue-prism-editor'
import { highlight, languages } from 'prismjs/components/prism-core'

import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-markup'
import 'vue-prism-editor/dist/prismeditor.min.css'
import 'prismjs/themes/prism.css'

/**
 * An editable code field — a prism-highlighted textarea, `v-model` on the string.
 *
 * `CodeArea` is the read-only half of the pair, for a block the user is meant to copy out;
 * this one is for markup they write. It highlights as markup with embedded javascript,
 * which is what an ad creative is, and it is not a form control: it brings no label or
 * validation state, so put it in a `FormItem`.
 */
withDefaults(
  defineProps<{
    lineNumbers?: boolean
    readonly?: boolean
    placeholder?: string
  }>(),
  { lineNumbers: true, readonly: false, placeholder: '' }
)

const code = defineModel<string>({ default: '' })

/**
 * Ad creatives are markup with an inline `<script>`, and the markup grammar highlights
 * the embedded javascript once that language is loaded — hence both imports above.
 */
function highlighter(value: string) {
  return highlight(value, languages.markup, 'markup')
}
</script>

<template>
  <PrismEditor
    v-model="code"
    class="code-editor"
    :highlight="highlighter"
    :line-numbers="lineNumbers"
    :readonly="readonly"
    :placeholder="placeholder"
  />
</template>

<style lang="scss" scoped>
.code-editor {
  /* el-form-item__content is a flex row — take the full width to fill it */
  width: 100%;
  min-height: 150px;
  max-height: 300px;
  padding: 8px;
  font-family: 'Fira Code', 'Fira Mono', Consolas, Menlo, Courier, monospace;
  font-size: 13px;
  line-height: 20px;
  color: var(--el-text-color-primary);
  background: var(--el-fill-color-light);
  border: 1px solid var(--el-border-color);
  border-radius: var(--el-border-radius-base);
}

:deep(.prism-editor__textarea:focus) {
  outline: none;
}

:deep(.prism-editor__line-numbers) {
  color: var(--el-text-color-placeholder);
}
</style>
